import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import * as posedetection from "@tensorflow-models/pose-detection";
import html2canvas from "html2canvas";
import "../css/Rom.css";
import secureLocalStorage from "react-secure-storage";
import AWS from "aws-sdk";
import { getProgress } from "../Api";

const RomV2 = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [romMessage, setRomMessage] = useState("No Detection Found.");
  const [updatedText, setUpdatedText] = useState("");
  const [detectedAngle, setDetectedAngle] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionComplete, setDetectionComplete] = useState(false);
  const [showCameraPrompt, setShowCameraPrompt] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [progress, setProgress] = useState(0);
  const [patientProgress, setPatientProgress] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [imageSaved, setImageSaved] = useState(false);
  const [capturedRom, setCapturedRom] = useState(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [isCountdown, setIsCountdown] = useState(false);

  const MOBILE_SCREEN_WIDTH_THRESHOLD = 768;
  const SHOW_DEVICE_DEBUG_INFO = false;

  const getPatientId = () => secureLocalStorage.getItem("patient_id");

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await getProgress(getPatientId());
        secureLocalStorage.setItem("week_no", response.patient_info.week_no);
        secureLocalStorage.setItem("patient_info", response.patient_info);
        setPatientProgress(response.patient_info.which_knee);
      } catch (error) {
        console.error("Error fetching progress:", error);
      }
    };

    fetchProgress();
  }, []);

  AWS.config.update({
    accessKeyId: "AKIAQEIP3MK7YSCRON7B",
    secretAccessKey: "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",
    region: "ap-southeast-2",
  });

  const s3 = new AWS.S3();

  const setupCamera = async () => {
    setShowCameraPrompt(true);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error(
        "Browser API navigator.mediaDevices.getUserMedia not available"
      );
    }

    const video = videoRef.current;
    if (video && video.srcObject) {
      video.play();
    }

    video.width = window.innerWidth;
    video.height = window.innerHeight;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: video.width, height: video.height },
      });

      // Safari-specific workaround
      if (typeof stream.getTracks === "function") {
        const tracks = stream.getTracks();
        if (tracks.length > 0 && tracks[0].readyState === "ended") {
          throw new Error("Camera stream ended unexpectedly");
        }
      }

      video.srcObject = stream;
      setShowCameraPrompt(false);

      return new Promise((resolve) => {
        video.onloadedmetadata = () => resolve(video);
      });
    } catch (error) {
      console.error("Error accessing camera: ", error);
      setCameraError(true);
      return null;
    }
  };

  useEffect(() => {
    const loadMoveNet = async (whichKnee) => {
      if (!whichKnee) {
        console.error("Patient progress is not available.");
        return;
      }

      try {
        await tf.setBackend("webgpu");
        await tf.ready();
      } catch (error) {
        console.error("Error initializing TensorFlow.js backend:", error);
        return;
      }

      // Handle WebSocket connection
      const socket = new WebSocket('ws://localhost:3000/ws');
      socket.onopen = () => {
        console.log('WebSocket connection established');
      };
      socket.onclose = (event) => {
        if (event.wasClean) {
          console.log('WebSocket connection closed cleanly');
        } else {
          console.error('WebSocket connection closed unexpectedly');
        }
      };
      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      const detectorConfig = {
        modelType: posedetection.movenet.modelType.LIGHTNING,
      };

      const detector = await posedetection.createDetector(
        posedetection.SupportedModels.MoveNet,
        detectorConfig
      );
      const video = await setupCamera();

      if (video) {
        video.play();
        detectPoseInRealTime(video, detector, whichKnee);
      } else {
        setShowCameraPrompt(true);
      }
    };

    if (patientProgress) {
      loadMoveNet(patientProgress);
    }
  }, [patientProgress]);

  const retryCameraAccess = async () => {
    setCameraError(false);
    const video = await setupCamera();
    if (video) {
      video.play();
    } else {
      setShowCameraPrompt(true);
    }
  };

  const detectPoseInRealTime = (video, detector, whichKnee) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    let lastDetectionTime = 0;

    const poseDetectionFrame = async () => {
      const poses = await detector.estimatePoses(video);

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (poses && poses.length > 0) {
        const pose = poses[0];
        const currentTime = Date.now();

        if (currentTime - lastDetectionTime >= 10000) {
          detectRightHand(pose);
          lastDetectionTime = currentTime;
        }

        detectLeg(pose, ctx, whichKnee);
      } else {
        setRomMessage("No Detection Found.");
        setDetectedAngle(null);
        setIsDetecting(false);
      }

      requestAnimationFrame(poseDetectionFrame);
    };

    poseDetectionFrame();
  };

  const detectRightHand = (pose) => {};

  const updateRomMessage = (angle) => {
    const messages = [
      { condition: angle < 83, message: "You're getting there! Keep working towards 83° to climb stairs safely." },
      { condition: angle >= 83 && angle <= 89, message: "Awesome! You can climb stairs now." },
      { condition: angle === 90, message: "Great job! You've reached 90°, safe to descend stairs." },
      { condition: angle >= 91 && angle <= 104, message: "You're almost there! Keep going." },
      { condition: angle === 105, message: "Amazing! Standing up from a chair is easier." },
      { condition: angle >= 106 && angle <= 114, message: "You're getting close to biking time." },
      { condition: angle === 115, message: "Fantastic! You’re ready to ride a bike!" },
      { condition: angle >= 116 && angle <= 134, message: "Keep pushing, full flexion is right around the corner." },
      { condition: angle === 135, message: "Full flexion achieved!" },
    ];

    const message = messages.find((msg) => msg.condition)?.message;
    if (message) setRomMessage(message);
  };

  const updateSavedText = (angle) => {
    const messages = [
      { condition: angle < 83, message: "You're getting there! Keep working towards 83° to climb stairs safely." },
      { condition: angle >= 83 && angle <= 89, message: "Awesome! You can climb stairs now." },
      { condition: angle === 90, message: "Great job! You've reached 90°, safe to descend stairs." },
      { condition: angle >= 91 && angle <= 104, message: "You're almost there! Keep going." },
      { condition: angle === 105, message: "Amazing! Standing up from a chair is easier." },
      { condition: angle >= 106 && angle <= 114, message: "You're getting close to biking time." },
      { condition: angle === 115, message: "Fantastic! You’re ready to ride a bike!" },
      { condition: angle >= 116 && angle <= 134, message: "Keep pushing, full flexion is right around the corner." },
      { condition: angle === 135, message: "Full flexion achieved!" },
    ];

    const message = messages.find((msg) => msg.condition)?.message;
    if (message) setUpdatedText(message);
  };

  let smoothedHip = { x: 0, y: 0 };
  let smoothedKnee = { x: 0, y: 0 };
  let smoothedAnkle = { x: 0, y: 0 };

  const STABLE_FRAME_THRESHOLD = 12;
  const CONFIDENCE_THRESHOLD = 0.5;
  const ANGLE_DEAD_ZONE = 10;
  const SMOOTHING_FACTOR = 0.8;
  const HIP_SMOOTHING_FACTOR = 0.6;

  let smoothedAngle = 0;
  let lastFrameAngle = 0;
  let stableFrameCount = 0;
  let angleLocked = false;

  const detectLeg = (pose, ctx, whichKnee) => {
    const keypoints = pose.keypoints;
    const side = whichKnee === "Left" ? "left" : "right";
    console.log(side);
    const hipKey = `${side}_hip`;
    const kneeKey = `${side}_knee`;
    const ankleKey = `${side}_ankle`;

    const hip = keypoints.find((point) => point.name === hipKey);
    const knee = keypoints.find((point) => point.name === kneeKey);
    const ankle = keypoints.find((point) => point.name === ankleKey);

    if (
      knee &&
      knee.score > CONFIDENCE_THRESHOLD &&
      ankle &&
      ankle.score > CONFIDENCE_THRESHOLD
    ) {
      if (!isDetecting) {
        setIsDetecting(true);
        setTimeout(() => {
          setIsDetecting(false);
          setDetectionComplete(true);
        }, 5000);
      }

      if (knee && hip) {
        let rawAngle = calculateAngle(hip, knee, ankle);

        smoothedAngle =
          smoothedAngle * (1 - SMOOTHING_FACTOR) + rawAngle * SMOOTHING_FACTOR;

        const roundedAngle = Math.round(smoothedAngle / 2) * 2;

        if (Math.abs(roundedAngle - lastFrameAngle) > ANGLE_DEAD_ZONE) {
          lastFrameAngle = roundedAngle;
          stableFrameCount = 0;
          angleLocked = false;
          setDetectedAngle(roundedAngle);
          updateRomMessage(roundedAngle);
        } else {
          stableFrameCount++;

          if (stableFrameCount >= STABLE_FRAME_THRESHOLD && !angleLocked) {
            angleLocked = true;
            setDetectedAngle(roundedAngle);
            updateRomMessage(roundedAngle);
          }
        }

        smoothedHip = smoothPoint(smoothedHip, hip, HIP_SMOOTHING_FACTOR);
        smoothedKnee = smoothPoint(smoothedKnee, knee, SMOOTHING_FACTOR);
        smoothedAnkle = smoothPoint(smoothedAnkle, ankle, SMOOTHING_FACTOR);

        drawAngleLine(
          ctx,
          smoothedHip,
          smoothedKnee,
          smoothedAnkle,
          roundedAngle
        );
      }
    } else {
      setRomMessage("No Detection Found.");
      setDetectedAngle(null);
      setIsDetecting(false);
      angleLocked = false;
    }
  };

  const smoothPoint = (smoothedPoint, currentPoint, smoothingFactor) => ({
    x: smoothedPoint.x * (1 - smoothingFactor) + currentPoint.x * smoothingFactor,
    y: smoothedPoint.y * (1 - smoothingFactor) + currentPoint.y * smoothingFactor,
  });

  const drawAngleLine = (ctx, hip, knee, ankle, roundedAngle) => {
    ctx.strokeStyle = "rgba(21, 169, 207, 0.70)";
    ctx.lineWidth = 4;

    ctx.beginPath();
    ctx.moveTo(hip.x, hip.y);
    ctx.lineTo(knee.x, knee.y);
    ctx.lineTo(ankle.x, ankle.y);
    ctx.stroke();

    const drawCircle = (x, y) => {
      ctx.fillStyle = "#FC6448";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.70)";
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    };

    drawCircle(hip.x, hip.y);
    drawCircle(knee.x, knee.y);
    drawCircle(ankle.x, ankle.y);

    ctx.font = "18px Arial";
    ctx.fillStyle = "blue";
    ctx.fillText(`${roundedAngle}°`, knee.x, knee.y - 10);
  };

  const calculateAngle = (hip, knee, ankle) => {
    const dy1 = hip.y - knee.y;
    const dx1 = hip.x - knee.x;
    const dy2 = ankle.y - knee.y;
    const dx2 = ankle.x - knee.x;

    const angleRad = Math.atan2(dy2, dx2) - Math.atan2(dy1, dx1);
    let angleDeg = Math.abs((angleRad * 180) / Math.PI);

    if (angleDeg > 180) angleDeg = 360 - angleDeg;

    return 180 - angleDeg;
  };

  const uploadToS3 = async (imageData) => {
    const patientId = secureLocalStorage.getItem("patient_id");
    const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
    const fileName = `rom-${patientId}-${timestamp}.png`;

    const params = {
      Bucket: "engivio-rom-uploads",
      Key: fileName,
      Body: Uint8Array.from(
        atob(imageData.replace(/^data:image\/\w+;base64,/, "")),
        (c) => c.charCodeAt(0)
      ),
      ContentEncoding: "base64",
      ContentType: "image/png",
    };

    try {
      const data = await s3.upload(params).promise();
      return data.Location;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  const getSpriteSrc = () => {
    if (detectedAngle === null || !detectedAngle) {
      return "../rom-sprites/ROM0.png";
    } else if (detectedAngle < 20) {
      return "../rom-sprites/ROM1.png";
    } else if (detectedAngle < 60) {
      return "../rom-sprites/ROM2.png";
    } else if (detectedAngle < 83) {
      return "../rom-sprites/ROM3.png";
    } else if (detectedAngle <= 90) {
      return "../rom-sprites/ROM4.png";
    } else if (detectedAngle <= 105) {
      return "../rom-sprites/ROM5.png";
    } else if (detectedAngle <= 115) {
      return "../rom-sprites/ROM6.png";
    } else if (detectedAngle <= 134) {
      return "../rom-sprites/ROM7.png";
    } else {
      return "../rom-sprites/ROM8.png";
    }
  };

  const saveData = async () => {
    const romValue = localStorage.getItem("last_angle_detected").replace("°", "");
    const imageUrl = await uploadToS3(screenshot);

    if (!imageUrl) {
      console.error("Failed to upload image to S3");
      return;
    }

    const data = {
      week_no: secureLocalStorage.getItem("week_no"),
      knee_flexion: 0,
      knee_extension: 0,
      hip_abduction: 0,
      hip_adduction: 0,
      hip_extension: 0,
      hip_flexion: 0,
      hip_rotation_ext: 0,
      hip_rotation_int: 0,
      patient_id: secureLocalStorage.getItem("patient_id"),
      rom_value: romValue,
      image: imageUrl,
    };
    console.log(data);
    try {
      const response = await fetch("https://api2.engiv.io/rom", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to save data");
      }
      console.log("Data saved successfully");
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  useEffect(() => {
    const checkMobileDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileByUserAgent = mobileRegex.test(userAgent);

      const isMobileByScreenSize = window.innerWidth < MOBILE_SCREEN_WIDTH_THRESHOLD;

      const hasTouchCapability =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;

      const info = {
        userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        isMobileByUserAgent,
        isMobileByScreenSize,
        hasTouchCapability,
        threshold: MOBILE_SCREEN_WIDTH_THRESHOLD,
      };
      setDeviceInfo(info);

      setIsMobileDevice(
        isMobileByUserAgent || isMobileByScreenSize || hasTouchCapability
      );
    };

    checkMobileDevice();

    window.addEventListener("resize", checkMobileDevice);

    return () => {
      window.removeEventListener("resize", checkMobileDevice);
    };
  }, []);

  const captureScreenshot = () => {
    const bottomData = document.querySelector(".bottomData");
    const captureCircleOuter = bottomData.querySelector(".capture-circle-outer");
    if (bottomData) {
      bottomData.style.background = "linear-gradient(to right, #FC6448, #FFF)";
      bottomData.style.transition = "background 2s ease-in-out";
      captureCircleOuter.style.display = "none";
    }

    setCapturedRom(romMessage);
    let countdown = 11;
    const countdownInterval = setInterval(() => {
      setIsCountdown(true);
      if (countdown > 0) {
        setProgress(countdown);
        countdown--;
      } else {
        clearInterval(countdownInterval);
        const container = document.querySelector(".canvas-container");
        html2canvas(container, {
          x: 0,
          y: 0,
          width: container.clientWidth,
          height: container.clientHeight,
          scale: window.devicePixelRatio,
        }).then((canvas) => {
          const image = canvas.toDataURL("image/png");
          setScreenshot(image);
          console.log(
            "Last detected angle:",
            document.querySelector("span.angle").textContent
          );
          localStorage.setItem(
            "last_angle_detected",
            document.querySelector("span.angle").textContent
          );
        });

        if (bottomData) {
          bottomData.style.background = "#ffffffb2";
          captureCircleOuter.style.display = "block";
        }
      }

      const countdownDisplay = [];
      for (let i = countdown; i > countdown - 5 && i > 0; i--) {
        countdownDisplay.push(
          <span key={i} className={i === countdown ? "active" : ""}>
            {i}
          </span>
        );
      }
      setProgress(countdownDisplay);
    }, 1000);
  };

  return (
    <div className="divContainer">
      {isMobileDevice ? (
        <div className="mobile-device-message">
          <div className="message-container">
            <div className="setup">
              <h2>Device Not Supported</h2>
              <p>
                This feature requires a larger screen and is currently only
                supported on laptops and desktops.
              </p>
              <p>
                For the best experience, please access this page from a device
                with a screen width of at least {MOBILE_SCREEN_WIDTH_THRESHOLD}
                px.
              </p>
              <img src="../ROM Illustration.png" width="300" alt="ROM Illustration" />
              <a href="/" className="home-button">
                Return to Home
              </a>

              {SHOW_DEVICE_DEBUG_INFO && (
                <div className="device-debug-info">
                  <h3>Device Detection Info</h3>
                  <p>
                    Screen Size: {deviceInfo.screenWidth}x
                    {deviceInfo.screenHeight}
                  </p>
                  <p>
                    Mobile by User Agent:{" "}
                    {deviceInfo.isMobileByUserAgent ? "Yes" : "No"}
                  </p>
                  <p>
                    Mobile by Screen Size:{" "}
                    {deviceInfo.isMobileByScreenSize ? "Yes" : "No"}
                  </p>
                  <p>
                    Has Touch Capability:{" "}
                    {deviceInfo.hasTouchCapability ? "Yes" : "No"}
                  </p>
                  <p>Screen Width Threshold: {deviceInfo.threshold}px</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {showCameraPrompt && (
            <div className="camera-overlay">
              <img className="top-right" src="../top-circle.png" alt="Top Circle" />
              <img className="bottom-left" src="../bottom-circle.png" alt="Bottom Circle" />
              <div className="setup">
                <h2>Set up your device</h2>
                <p>
                  Allow your camera to track your <br />
                  movement
                </p>
                <img src="../ROM Illustration.png" width="300" alt="ROM Illustration" />
                <button onClick={retryCameraAccess}>Continue</button>
              </div>
            </div>
          )}

          {cameraError && (
            <div className="camera-error-overlay">
              <div className="camera-overlay">
                <img className="top-right" src="../top-circle.png" alt="Top Circle" />
                <img className="bottom-left" src="../bottom-circle.png" alt="Bottom Circle" />
                <div className="setup">
                  <h2>Set up your device</h2>
                  <p>
                    Camera access is required. Please allow <br />
                    camera access and click Retry.
                  </p>
                  <img src="../ROM Illustration.png" width="300" alt="ROM Illustration" />
                  <button onClick={retryCameraAccess}>Retry</button>
                </div>
              </div>
            </div>
          )}
          <div className="customRomHeader">
            <a href="/">
              <img src="../menuIconRom.svg" width="27" alt="Menu Icon" />
              <img
                src="../Engivio Login Logo.svg"
                width="123"
                alt="Engivio Logo"
              />
            </a>
          </div>
          <div className="canvas-container">
            <video
              ref={videoRef}
              style={{ display: "none" }}
              autoPlay
              playsInline
              muted
            />
            <canvas
              ref={canvasRef}
              style={{
                width: "100%",
                height: "100%",
                border: "1px solid black",
              }}
            />

            {screenshot && (
              <div className="screenshot-modal">
                <div className="screenshot-container">
                  <img src={screenshot} alt="Screenshot Preview" />
                  <button
                    className="modal-save"
                    onClick={() => {
                      setScreenshot(null);
                      setImageSaved(true);
                      saveData().then(() => {
                        setImageSaved(false);
                        setShowSaveModal(true);
                      });
                    }}
                  >
                    Save
                  </button>
                  <button
                    className="modal-close"
                    onClick={() => setScreenshot(null)}
                  >
                    Take another one
                  </button>
                </div>
              </div>
            )}
            {imageSaved && (
              <div className="show-save-modal">
              <div className="modal-content">
                <h2>Please wait...</h2>
              </div>
            </div>
            )}
            {showSaveModal && (
              <div className="show-save-modal">
                <div className="modal-content">
                  <button
                    className="modal-close"
                    onClick={() => setShowSaveModal(false)}
                  >
                    &times;
                  </button>
                  <h2>Great Job!</h2>
                  {updateSavedText(localStorage.getItem("last_angle_detected"))}
                  <p>
                    You've reached{" "}
                    <b>{localStorage.getItem("last_angle_detected")}</b> today.
                  </p>
                </div>
              </div>
            )}

            <div className="sideData">
              <div className="romAngle">
                <p>
                  Angle:{" "}
                  <span className="angle">
                    {detectedAngle !== null ? detectedAngle : 0}°
                  </span>
                </p>
              </div>
              <div className="romMovement">
                <img
                  src={getSpriteSrc()}
                  style={{ transition: "opacity 0.5s ease-in-out" }}
                  alt="Knee Movement"
                />
              </div>
            </div>

            {/* <div className="sideLeftData">
              <div className="measurement">
                <div className="headings">
                  <div className="left-heading">Measurement</div>
                  <div className="right-heading">1/3</div>
                </div>
              </div>
            </div> */}

            <div className="bottomData fade3">
              <div className="countdown-container">
                <h3 className="countdown-number">{isCountdown && progress}</h3>
              </div>
              <div className="capture-circle-outer" onClick={captureScreenshot}>
                <div className="capture-circle-inner">
                  <img className="camera-capture" src="../captureIcon.png" alt="Capture Icon" />
                </div>
              </div>
            </div>
          </div>

          {isDetecting && <div className="progress-bar">Detecting...</div>}
          {detectionComplete && (
            <div className="notification-overlay">Detection complete!</div>
          )}
        </>
      )}
    </div>
  );
};

export default RomV2;
